import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../contexts/ThemeContext';
import { getThemeColors } from '../utils/theme';
import i18n from '../i18n/config';
import { API_ENDPOINTS } from '../config.js';
import Navbar from './Navbar';
import TrialNotification from './TrialNotification';
import { authenticatedFetch, authenticatedFetchJson } from '../utils/api.js';

export default function OrganizationManagement() {
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const colors = getThemeColors(isDark);
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [members, setMembers] = useState([]);
  const [pendingInvitations, setPendingInvitations] = useState([]);
  const [loadingInvitations, setLoadingInvitations] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [scriptMode, setScriptMode] = useState('latin');
  const [userInfo, setUserInfo] = useState(null);
  const [companyData, setCompanyData] = useState(null);
  
  // Invite form state
  const [inviteForm, setInviteForm] = useState({ email: '', role: 'member' });
  const [inviteLoading, setInviteLoading] = useState(false);

  // Role change state
  const [roleChangeLoading, setRoleChangeLoading] = useState({});
  const [deleteInvitationLoading, setDeleteInvitationLoading] = useState({});
  
  // Responsive state
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 1024);

  // Handle window resize for responsive design
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 1024);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Helper function to format dates based on current locale
  const formatDate = (dateString) => {
    const localeMap = {
      'sr': 'sr-RS',
      'zh': 'zh-CN',
      'en': 'en-US'
    };
    const locale = localeMap[i18n.language] || 'en-US';
    return new Date(dateString).toLocaleDateString(locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Helper functions to map between UI ('member') and API ('user') role formats
  const roleToApi = (role) => {
    // Map UI role to API role
    return role === 'member' ? 'user' : role;
  };

  const roleFromApi = (role) => {
    // Map API role to UI role
    return role === 'user' ? 'member' : role;
  };

  // Fetch user info and company data
  useEffect(() => {
    const loadUserInfo = async () => {
      try {
        const res = await fetch(API_ENDPOINTS.PROFILE, {
          method: 'GET',
          credentials: 'include',
        });
        
        if (res.ok) {
          const data = await res.json();
          if (data.user) {
            setUserInfo({
              email: data.user.email,
              userId: data.user.id,
              companyId: data.company?.id
            });
          }
          if (data.company) {
            setCompanyData(data.company);
          }
        } else if (res.status === 401) {
          navigate('/login');
        }
      } catch (error) {
        console.error('Error fetching user info:', error);
      }
    };

    loadUserInfo();
  }, [navigate]);

  // Load organization members
  useEffect(() => {
    const loadMembers = async () => {
      try {
        const data = await authenticatedFetchJson(API_ENDPOINTS.ORGANIZATION_MEMBERS);
        // Map API roles ('user') to UI roles ('member') for display
        const mappedMembers = (data.members || []).map(member => ({
          ...member,
          role: roleFromApi(member.role)
        }));
        setMembers(mappedMembers);
        setLoading(false);
      } catch (err) {
        if (err.message && err.message.includes('Unauthorized')) {
          return;
        }
        if (err.message && err.message.includes('403')) {
          setError(t('organization.noPermission'));
          setLoading(false);
          return;
        }
        console.error('Error loading members:', err);
        setError(t('organization.errorLoadingMembers'));
        setLoading(false);
      }
    };

    loadMembers();
  }, [t]);

      // Load pending invitations
  useEffect(() => {
    const loadInvitations = async () => {
      setLoadingInvitations(true);
      try {
        const data = await authenticatedFetchJson(API_ENDPOINTS.ORGANIZATION_INVITATIONS);
        // Map API roles ('user') to UI roles ('member') for display
        const mappedInvitations = (data.invitations || []).map(invitation => ({
          ...invitation,
          role: roleFromApi(invitation.role)
        }));
        setPendingInvitations(mappedInvitations);
      } catch (err) {
        if (err.message && err.message.includes('Unauthorized')) {
          return;
        }
        if (!err.message || !err.message.includes('403')) {
          console.error('Error loading invitations:', err);
        }
      } finally {
        setLoadingInvitations(false);
      }
    };

    loadInvitations();
  }, []);

  const handleInviteMember = async (e) => {
    e.preventDefault();
    setInviteLoading(true);
    setError('');
    setSuccess('');

    try {
      // Map UI role ('member') to API role ('user') before sending
      const apiRole = roleToApi(inviteForm.role);
      
      const response = await authenticatedFetch(API_ENDPOINTS.ORGANIZATION_INVITE, {
        method: 'POST',
        body: JSON.stringify({
          email: inviteForm.email,
          role: apiRole
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || t('organization.errorSendingInvitation'));
      }

      setSuccess(t('organization.invitationSent'));
      setInviteForm({ email: '', role: 'member' });
      
      // Reload members list and invitations
      const membersData = await authenticatedFetchJson(API_ENDPOINTS.ORGANIZATION_MEMBERS);
      // Map API roles ('user') to UI roles ('member') for display
      const mappedMembers = (membersData.members || []).map(member => ({
        ...member,
        role: roleFromApi(member.role)
      }));
      setMembers(mappedMembers);
      
      const invitationsData = await authenticatedFetchJson(API_ENDPOINTS.ORGANIZATION_INVITATIONS);
      // Map invitation roles as well
      const mappedInvitations = (invitationsData.invitations || []).map(invitation => ({
        ...invitation,
        role: roleFromApi(invitation.role)
      }));
      setPendingInvitations(mappedInvitations);

    } catch (err) {
      if (err.message && err.message.includes('Unauthorized')) {
        return;
      }
      setError(err.message);
    } finally {
      setInviteLoading(false);
    }
  };

  const handleRemoveMember = async (memberId, memberEmail) => {
    if (!window.confirm(t('organization.confirmRemoveMember', { email: memberEmail }))) {
      return;
    }

    try {
      const response = await authenticatedFetch(`${API_ENDPOINTS.ORGANIZATION_REMOVE_MEMBER}/${memberId}`, {
        method: 'DELETE'
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || t('organization.errorRemovingMember'));
      }

      setSuccess(t('organization.memberRemovedSuccess'));
      setMembers(members.filter(member => member.id !== memberId));

    } catch (err) {
      if (err.message && err.message.includes('Unauthorized')) {
        return;
      }
      setError(err.message);
    }
  };

  const handleChangeRole = async (memberId, newRole) => {
    setRoleChangeLoading(prev => ({ ...prev, [memberId]: true }));

    try {
      // Map UI role ('member') to API role ('user') before sending
      const apiRole = roleToApi(newRole);
      
      const response = await authenticatedFetch(`${API_ENDPOINTS.ORGANIZATION_CHANGE_ROLE}/${memberId}/role`, {
        method: 'PUT',
        body: JSON.stringify({ role: apiRole })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || t('organization.errorChangingRole'));
      }

      setSuccess(t('organization.roleChangedSuccess'));
      // Update local state with UI role format
      setMembers(members.map(member => 
        member.id === memberId ? { ...member, role: newRole } : member
      ));

    } catch (err) {
      if (err.message && err.message.includes('Unauthorized')) {
        return;
      }
      setError(err.message);
    } finally {
      setRoleChangeLoading(prev => ({ ...prev, [memberId]: false }));
    }
  };

  const handleDeleteInvitation = async (invitationId, invitationEmail) => {
    if (!window.confirm(t('organization.confirmDeleteInvitation', { email: invitationEmail }))) {
      return;
    }

    setDeleteInvitationLoading(prev => ({ ...prev, [invitationId]: true }));
    setError('');
    setSuccess('');

    try {
      const response = await authenticatedFetch(`${API_ENDPOINTS.ORGANIZATION_DELETE_INVITATION}/${invitationId}`, {
        method: 'DELETE'
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || t('organization.errorDeletingInvitation'));
      }

      setSuccess(t('organization.invitationDeletedSuccess'));
      // Remove invitation from local state
      setPendingInvitations(pendingInvitations.filter(inv => inv.id !== invitationId));

    } catch (err) {
      if (err.message && err.message.includes('Unauthorized')) {
        return;
      }
      setError(err.message);
    } finally {
      setDeleteInvitationLoading(prev => ({ ...prev, [invitationId]: false }));
    }
  };

  if (loading) {
    return (
      <div style={{ 
        minHeight: '100vh', 
        background: colors.backgroundLight, 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center' 
      }}>
        <div style={{ 
          fontSize: 18, 
          color: colors.primary,
          fontWeight: 500
        }}>
          {t('organization.loadingMembers')}
        </div>
      </div>
    );
  }

  return (
    <div style={{ 
      minHeight: '100vh', 
      background: colors.backgroundLight,
      transition: 'background-color 0.3s ease'
    }}>
      <TrialNotification companyData={companyData} />
      <Navbar scriptMode={scriptMode} onScriptModeChange={setScriptMode} />
      
      <div style={{ 
        padding: '2rem', 
        maxWidth: '1400px', 
        margin: '0 auto'
      }}>
        {/* Header */}
        <div style={{ 
          marginBottom: '2rem',
          display: 'flex',
          alignItems: 'center',
          gap: '1rem'
        }}>
          <button
            onClick={() => navigate('/dashboard')}
            style={{
              background: colors.card,
              border: `1px solid ${colors.border}`,
              borderRadius: '8px',
              padding: '10px 16px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontSize: '14px',
              color: colors.text,
              fontWeight: 500,
              transition: 'all 0.2s ease',
              boxShadow: `0 1px 2px ${colors.shadow}`
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = colors.backgroundHover;
              e.target.style.borderColor = colors.primary;
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = colors.card;
              e.target.style.borderColor = colors.border;
            }}
          >
            <svg width="16" height="16" fill="currentColor" viewBox="0 0 24 24">
              <path d="M19 7v4H5.83l3.58-3.59L8 6l-6 6 6 6 1.41-1.41L5.83 13H21V7z"/>
            </svg>
            {t('organization.backToDashboard')}
          </button>
          
          <h1 style={{ 
            fontSize: '2rem', 
            fontWeight: 800, 
            color: colors.text, 
            margin: 0,
            flex: 1
          }}>
            {t('organization.title')}
          </h1>
        </div>

        {/* Error/Success Messages */}
        {error && (
          <div style={{
            background: isDark ? '#7f1d1d' : '#fef2f2',
            border: `1px solid ${isDark ? '#991b1b' : '#fecaca'}`,
            borderRadius: '8px',
            padding: '14px 18px',
            marginBottom: '1.5rem',
            color: colors.error,
            fontSize: '14px',
            fontWeight: 500,
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            boxShadow: `0 1px 3px ${colors.shadow}`
          }}>
            <svg width="20" height="20" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
            </svg>
            {error}
          </div>
        )}

        {success && (
          <div style={{
            background: isDark ? '#14532d' : '#f0fdf4',
            border: `1px solid ${isDark ? '#166534' : '#bbf7d0'}`,
            borderRadius: '8px',
            padding: '14px 18px',
            marginBottom: '1.5rem',
            color: colors.success,
            fontSize: '14px',
            fontWeight: 500,
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            boxShadow: `0 1px 3px ${colors.shadow}`
          }}>
            <svg width="20" height="20" fill="currentColor" viewBox="0 0 24 24">
              <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
            </svg>
            {success}
          </div>
        )}

        {/* Pending Invitations Section */}
        {pendingInvitations.length > 0 && (
          <div style={{
            background: colors.card,
            borderRadius: '12px',
            padding: '1.5rem',
            boxShadow: `0 2px 8px ${colors.shadow}`,
            marginBottom: '2rem',
            border: `1px solid ${colors.border}`
          }}>
            <h2 style={{ 
              fontSize: '1.25rem', 
              fontWeight: 700, 
              color: colors.text, 
              marginBottom: '1rem',
              display: 'flex',
              alignItems: 'center',
              gap: '10px'
            }}>
              <span style={{
                background: isDark ? '#78350f' : '#fef3c7',
                color: isDark ? '#fbbf24' : '#92400e',
                padding: '4px 10px',
                borderRadius: '6px',
                fontSize: '12px',
                fontWeight: 600
              }}>
                {pendingInvitations.length}
              </span>
              {t('organization.pendingInvitations')}
            </h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {pendingInvitations.map(invitation => {
                const expiresAt = new Date(invitation.expiresAt);
                const now = new Date();
                const daysUntilExpiry = Math.ceil((expiresAt - now) / (1000 * 60 * 60 * 24));
                const isExpiringSoon = daysUntilExpiry <= 2;
                
                return (
                  <div
                    key={invitation.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '1rem',
                      border: `1px solid ${colors.border}`,
                      borderRadius: '8px',
                      background: isExpiringSoon 
                        ? (isDark ? '#78350f' : '#fef3c7') 
                        : (isDark ? '#1e3a5f' : '#eff6ff'),
                      transition: 'all 0.2s ease'
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      <div style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '0.75rem', 
                        marginBottom: '0.5rem',
                        flexWrap: 'wrap'
                      }}>
                        <span style={{ 
                          fontWeight: 600, 
                          color: colors.text,
                          fontSize: '15px'
                        }}>
                          {invitation.email}
                        </span>
                        <span style={{
                          background: invitation.role === 'admin' 
                            ? (isDark ? '#7f1d1d' : '#fee2e2')
                            : (isDark ? '#064e3b' : '#d1fae5'),
                          color: invitation.role === 'admin' 
                            ? (isDark ? '#fca5a5' : '#dc2626')
                            : (isDark ? '#6ee7b7' : '#059669'),
                          padding: '4px 10px',
                          borderRadius: '12px',
                          fontSize: '12px',
                          fontWeight: 600
                        }}>
                          {invitation.role === 'admin' 
                            ? t('organization.administrator') 
                            : t('organization.member')}
                        </span>
                        {isExpiringSoon && (
                          <span style={{
                            background: isDark ? '#7c2d12' : '#fed7aa',
                            color: isDark ? '#fdba74' : '#c2410c',
                            padding: '4px 10px',
                            borderRadius: '12px',
                            fontSize: '12px',
                            fontWeight: 600
                          }}>
                            {t('organization.expiresIn', { 
                              days: daysUntilExpiry, 
                              daysText: daysUntilExpiry === 1 
                                ? t('organization.day') 
                                : t('organization.days') 
                            })}
                          </span>
                        )}
                      </div>
                      <div style={{ 
                        fontSize: '13px', 
                        color: colors.textSecondary,
                        display: 'flex',
                        gap: '1rem',
                        flexWrap: 'wrap'
                      }}>
                        <span>
                          {t('organization.sent')}: {formatDate(invitation.createdAt)}
                        </span>
                        <span>•</span>
                        <span>
                          {t('organization.expires')}: {formatDate(expiresAt)}
                        </span>
                      </div>
                    </div>
                    <button
                      onClick={() => handleDeleteInvitation(invitation.id, invitation.email)}
                      disabled={deleteInvitationLoading[invitation.id]}
                      style={{
                        background: deleteInvitationLoading[invitation.id] ? colors.textLight : colors.error,
                        color: '#fff',
                        border: 'none',
                        borderRadius: '6px',
                        padding: '8px 16px',
                        fontSize: '13px',
                        fontWeight: 600,
                        cursor: deleteInvitationLoading[invitation.id] ? 'not-allowed' : 'pointer',
                        transition: 'all 0.2s ease',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        boxShadow: deleteInvitationLoading[invitation.id] ? 'none' : `0 1px 2px ${colors.shadow}`,
                        opacity: deleteInvitationLoading[invitation.id] ? 0.6 : 1
                      }}
                      onMouseEnter={(e) => {
                        if (!deleteInvitationLoading[invitation.id]) {
                          e.target.style.background = isDark ? '#dc2626' : '#b91c1c';
                          e.target.style.transform = 'translateY(-1px)';
                          e.target.style.boxShadow = `0 2px 4px ${colors.shadowHover}`;
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!deleteInvitationLoading[invitation.id]) {
                          e.target.style.background = colors.error;
                          e.target.style.transform = 'translateY(0)';
                          e.target.style.boxShadow = `0 1px 2px ${colors.shadow}`;
                        }
                      }}
                    >
                      {deleteInvitationLoading[invitation.id] ? (
                        <svg width="14" height="14" fill="currentColor" viewBox="0 0 24 24" style={{ animation: 'spin 1s linear infinite' }}>
                          <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/>
                        </svg>
                      ) : (
                        <svg width="14" height="14" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
                        </svg>
                      )}
                      {t('organization.remove')}
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div style={{ 
          display: 'grid', 
          gap: '2rem', 
          gridTemplateColumns: isMobile ? '1fr' : '1fr 2fr'
        }}>
          {/* Invite New Member Card */}
          <div style={{
            background: colors.card,
            borderRadius: '12px',
            padding: '1.5rem',
            boxShadow: `0 2px 8px ${colors.shadow}`,
            height: 'fit-content',
            border: `1px solid ${colors.border}`
          }}>
            <h2 style={{ 
              fontSize: '1.25rem', 
              fontWeight: 700, 
              color: colors.text, 
              marginBottom: '1.5rem',
              display: 'flex',
              alignItems: 'center',
              gap: '10px'
            }}>
              <svg width="24" height="24" fill={colors.primary} viewBox="0 0 24 24">
                <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/>
              </svg>
              {t('organization.inviteNewMember')}
            </h2>

            <form onSubmit={handleInviteMember} style={{ 
              display: 'flex', 
              flexDirection: 'column', 
              gap: '1.25rem' 
            }}>
              <div>
                <label style={{ 
                  display: 'block', 
                  fontSize: '14px', 
                  fontWeight: 600, 
                  color: colors.text, 
                  marginBottom: '8px' 
                }}>
                  {t('organization.emailAddress')}
                </label>
                <input
                  type="email"
                  value={inviteForm.email}
                  onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '12px 14px',
                    border: `1px solid ${colors.border}`,
                    borderRadius: '8px',
                    fontSize: '14px',
                    background: colors.background,
                    color: colors.text,
                    outline: 'none',
                    transition: 'all 0.2s ease',
                    boxSizing: 'border-box'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = colors.primary;
                    e.target.style.boxShadow = `0 0 0 3px ${colors.primary}20`;
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = colors.border;
                    e.target.style.boxShadow = 'none';
                  }}
                  required
                  placeholder={t('organization.emailPlaceholder')}
                />
              </div>

              <div>
                <label style={{ 
                  display: 'block', 
                  fontSize: '14px', 
                  fontWeight: 600, 
                  color: colors.text, 
                  marginBottom: '8px' 
                }}>
                  {t('organization.role')}
                </label>
                <select
                  value={inviteForm.role}
                  onChange={(e) => setInviteForm({ ...inviteForm, role: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '12px 14px',
                    border: `1px solid ${colors.border}`,
                    borderRadius: '8px',
                    fontSize: '14px',
                    background: colors.background,
                    color: colors.text,
                    outline: 'none',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    boxSizing: 'border-box'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = colors.primary;
                    e.target.style.boxShadow = `0 0 0 3px ${colors.primary}20`;
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = colors.border;
                    e.target.style.boxShadow = 'none';
                  }}
                >
                  <option value="member">{t('organization.member')}</option>
                  <option value="admin">{t('organization.administrator')}</option>
                </select>
              </div>

              <button
                type="submit"
                disabled={inviteLoading}
                style={{
                  background: inviteLoading ? colors.textLight : colors.primary,
                  color: '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '12px 24px',
                  fontSize: '15px',
                  fontWeight: 600,
                  cursor: inviteLoading ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  boxShadow: inviteLoading ? 'none' : `0 2px 4px ${colors.shadow}`
                }}
                onMouseEnter={(e) => {
                  if (!inviteLoading) {
                    e.target.style.background = colors.primaryHover;
                    e.target.style.transform = 'translateY(-1px)';
                    e.target.style.boxShadow = `0 4px 8px ${colors.shadowHover}`;
                  }
                }}
                onMouseLeave={(e) => {
                  if (!inviteLoading) {
                    e.target.style.background = colors.primary;
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = `0 2px 4px ${colors.shadow}`;
                  }
                }}
              >
                {inviteLoading ? (
                  <>
                    <svg width="16" height="16" fill="currentColor" viewBox="0 0 24 24" style={{ animation: 'spin 1s linear infinite' }}>
                      <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/>
                    </svg>
                    {t('organization.sendingInvitation')}
                  </>
                ) : (
                  <>
                    <svg width="16" height="16" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/>
                    </svg>
                    {t('organization.sendInvitation')}
                  </>
                )}
              </button>
            </form>
          </div>

          {/* Members List Card */}
          <div style={{
            background: colors.card,
            borderRadius: '12px',
            padding: '1.5rem',
            boxShadow: `0 2px 8px ${colors.shadow}`,
            border: `1px solid ${colors.border}`
          }}>
            <h2 style={{ 
              fontSize: '1.25rem', 
              fontWeight: 700, 
              color: colors.text, 
              marginBottom: '1.5rem',
              display: 'flex',
              alignItems: 'center',
              gap: '10px'
            }}>
              <svg width="24" height="24" fill={colors.primary} viewBox="0 0 24 24">
                <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z"/>
              </svg>
              {t('organization.organizationMembers')}
              <span style={{
                background: colors.primary,
                color: '#fff',
                padding: '4px 10px',
                borderRadius: '6px',
                fontSize: '12px',
                fontWeight: 600,
                marginLeft: '8px'
              }}>
                {members.length}
              </span>
            </h2>

            {members.length === 0 ? (
              <div style={{ 
                textAlign: 'center', 
                padding: '3rem 2rem', 
                color: colors.textSecondary 
              }}>
                <svg width="64" height="64" fill={colors.textLight} viewBox="0 0 24 24" style={{ margin: '0 auto 1rem' }}>
                  <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z"/>
                </svg>
                <div style={{ fontSize: '16px', fontWeight: 500 }}>
                  {t('organization.noMembers')}
                </div>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {members.map(member => (
                  <div
                    key={member.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '1.25rem',
                      border: `1px solid ${colors.border}`,
                      borderRadius: '10px',
                      background: member.id === userInfo?.userId 
                        ? (isDark ? '#1e3a5f' : '#eff6ff')
                        : colors.card,
                      transition: 'all 0.2s ease',
                      boxShadow: `0 1px 2px ${colors.shadow}`
                    }}
                    onMouseEnter={(e) => {
                      if (member.id !== userInfo?.userId) {
                        e.currentTarget.style.background = colors.backgroundHover;
                        e.currentTarget.style.boxShadow = `0 2px 4px ${colors.shadowHover}`;
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (member.id !== userInfo?.userId) {
                        e.currentTarget.style.background = colors.card;
                        e.currentTarget.style.boxShadow = `0 1px 2px ${colors.shadow}`;
                      }
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      <div style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '0.75rem', 
                        marginBottom: '0.5rem',
                        flexWrap: 'wrap'
                      }}>
                        <span style={{ 
                          fontWeight: 600, 
                          color: colors.text,
                          fontSize: '15px'
                        }}>
                          {member.email}
                        </span>
                        {member.id === userInfo?.userId && (
                          <span style={{
                            background: colors.primary,
                            color: '#fff',
                            fontSize: '12px',
                            padding: '4px 10px',
                            borderRadius: '12px',
                            fontWeight: 600
                          }}>
                            {t('organization.you')}
                          </span>
                        )}
                        <span style={{
                          background: member.role === 'admin' 
                            ? (isDark ? '#7f1d1d' : '#fee2e2')
                            : (isDark ? '#064e3b' : '#d1fae5'),
                          color: member.role === 'admin' 
                            ? (isDark ? '#fca5a5' : '#dc2626')
                            : (isDark ? '#6ee7b7' : '#059669'),
                          padding: '4px 10px',
                          borderRadius: '12px',
                          fontSize: '12px',
                          fontWeight: 600
                        }}>
                          {member.role === 'admin' 
                            ? t('organization.administrator') 
                            : t('organization.member')}
                        </span>
                        <span style={{
                          background: member.isVerified 
                            ? (isDark ? '#064e3b' : '#d1fae5')
                            : (isDark ? '#78350f' : '#fef3c7'),
                          color: member.isVerified 
                            ? (isDark ? '#6ee7b7' : '#059669')
                            : (isDark ? '#fbbf24' : '#d97706'),
                          padding: '4px 10px',
                          borderRadius: '12px',
                          fontSize: '12px',
                          fontWeight: 600
                        }}>
                          {member.isVerified 
                            ? t('organization.verified') 
                            : t('organization.pendingVerification')}
                        </span>
                      </div>
                      <div style={{ 
                        fontSize: '13px', 
                        color: colors.textSecondary,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem'
                      }}>
                        <svg width="14" height="14" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                        </svg>
                        {t('organization.joined')}: {formatDate(member.createdAt)}
                      </div>
                    </div>

                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '0.75rem' 
                    }}>
                      {/* Role Change Dropdown */}
                      {member.id !== userInfo?.userId && (
                        <select
                          value={member.role}
                          onChange={(e) => handleChangeRole(member.id, e.target.value)}
                          disabled={roleChangeLoading[member.id]}
                          style={{
                            padding: '8px 12px',
                            borderRadius: '6px',
                            border: `1px solid ${colors.border}`,
                            fontSize: '13px',
                            background: colors.background,
                            color: colors.text,
                            cursor: roleChangeLoading[member.id] ? 'not-allowed' : 'pointer',
                            opacity: roleChangeLoading[member.id] ? 0.6 : 1,
                            transition: 'all 0.2s ease'
                          }}
                          onFocus={(e) => {
                            e.target.style.borderColor = colors.primary;
                          }}
                          onBlur={(e) => {
                            e.target.style.borderColor = colors.border;
                          }}
                        >
                          <option value="member">{t('organization.member')}</option>
                          <option value="admin">{t('organization.administrator')}</option>
                        </select>
                      )}

                      {/* Remove Member Button */}
                      {member.id !== userInfo?.userId && member.role !== 'admin' && (
                        <button
                          onClick={() => handleRemoveMember(member.id, member.email)}
                          style={{
                            background: colors.error,
                            color: '#fff',
                            border: 'none',
                            borderRadius: '6px',
                            padding: '8px 16px',
                            fontSize: '13px',
                            fontWeight: 600,
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            boxShadow: `0 1px 2px ${colors.shadow}`
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.background = isDark ? '#dc2626' : '#b91c1c';
                            e.target.style.transform = 'translateY(-1px)';
                            e.target.style.boxShadow = `0 2px 4px ${colors.shadowHover}`;
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.background = colors.error;
                            e.target.style.transform = 'translateY(0)';
                            e.target.style.boxShadow = `0 1px 2px ${colors.shadow}`;
                          }}
                        >
                          <svg width="14" height="14" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
                          </svg>
                          {t('organization.remove')}
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
